/**
 * OnboardingController — /api/v1/onboarding
 * Mirrors: src/Aidepo.Api/Controllers/OnboardingController.cs
 *
 * Phase 4.5 — состояние онбординга (case-44). Прогресс walkthrough'а
 * сохраняется на бэке, чтобы прерванный проход продолжался с того же места.
 */
import { apiGet, apiPatch, apiPost } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

export type OnboardingMode = 'jit' | 'walkthrough';

/** Прогресс — произвольный JSON-объект; структуру задаёт конкретный экран. */
export type OnboardingProgress = Record<string, unknown>;

/** Сессия онбординга. */
export interface OnboardingSession {
  readonly id: Uuid;
  readonly mode: OnboardingMode;
  readonly progress: OnboardingProgress;
  readonly startedAt: IsoDateTime;
  readonly completedAt: IsoDateTime | null;
}

export interface CurrentOnboardingResponse {
  readonly session: OnboardingSession | null;
}

/** POST /api/v1/onboarding/start — начать сессию. */
export function startOnboarding(
  mode: OnboardingMode,
  progress?: OnboardingProgress,
): Promise<OnboardingSession> {
  return apiPost<OnboardingSession, { mode: OnboardingMode; progress?: OnboardingProgress }>(
    '/api/v1/onboarding/start',
    progress !== undefined ? { mode, progress } : { mode },
  );
}

/** GET /api/v1/onboarding/current — последняя незавершённая сессия режима. */
export function getCurrentOnboarding(mode: OnboardingMode): Promise<CurrentOnboardingResponse> {
  return apiGet<CurrentOnboardingResponse>(`/api/v1/onboarding/current?mode=${mode}`);
}

/** PATCH /api/v1/onboarding/{id} — сохранить прогресс (при прерывании). */
export function patchOnboarding(
  id: Uuid,
  progress: OnboardingProgress,
): Promise<OnboardingSession> {
  return apiPatch<OnboardingSession, { progress: OnboardingProgress }>(
    `/api/v1/onboarding/${id}`,
    { progress },
  );
}

/** POST /api/v1/onboarding/{id}/complete — завершить сессию. */
export function completeOnboarding(id: Uuid): Promise<OnboardingSession> {
  return apiPost<OnboardingSession>(`/api/v1/onboarding/${id}/complete`);
}
